import React, { useState } from 'react';

import { HomeOutlined } from '@ant-design/icons';
import { RiCarLine, RiFileTextLine, RiScales3Line } from '@remixicon/react';
import { Steps as AntdSteps } from 'antd';

import { BrancheDto } from 'lib_api/lib/api/gen';

import { useApi } from 'hooks/ApiStoreContext';
import { useEnvironnement } from 'hooks/EnvironementStoreContext';
import { useRefEtatDetails } from 'hooks/referentiels/useRefEtatDetails';
import FicheInfractionForm from 'pages/gardien-fourriere/FicheDescriptive/FicheInfractionForm/FicheInfractionForm';
import { shouldDisplayJusticeFeatureBasedOnBranche } from 'utils/justice/methods';

import FicheFourriereForm from './FicheFourriereForm/FicheFourriereForm';
import FicheInfractionFormSansJustice from './FicheInfractionFormSansJustice/FicheInfractionFormSansJustice';
import FicheJudiciaireForm from './FicheJudiciaireForm/FicheJudiciaireForm';
import FicheVehiculeForm from './FicheVehiculeForm/FicheVehiculeForm';
import { userSummaryConfirm } from './SummaryConfirm/SummaryConfirm';
import {
  FicheDescriptiveFormValues,
  FicheFourriereFormValues,
  FicheInfractionFormValues,
  FicheJudiciaireFormValues,
  FicheVehiculeFormValues,
} from './types';
import { initialFicheDescriptiveFormValues } from './utils';

import './FicheDescriptive.less';

const { Step } = AntdSteps;
export type FicheDescriptiveFormProps = {
  onCreationSucceed: () => void;
  branche?: BrancheDto;
};

export default function FicheDescriptiveForm({
  onCreationSucceed,
  branche,
}: FicheDescriptiveFormProps): React.ReactElement {
  const [indexTab, setIndexTab] = useState(0);
  const [data, setData] = useState<FicheDescriptiveFormValues>(
    initialFicheDescriptiveFormValues(),
  );
  const { ReferentielControllerApi: refController } = useApi();
  const etatDetails = useRefEtatDetails(data.ficheVehicule.etat);
  const [{ JUSTICE_ACTIVE_BOOL }] = useEnvironnement();

  const goNextTab = (): Promise<void> => {
    return new Promise(() => {
      setIndexTab(indexTab + 1);
    });
  };
  const goPreviousTab = (): Promise<void> => {
    return new Promise(() => {
      setIndexTab(indexTab - 1);
    });
  };

  const userConfirm = (): Promise<void> => {
    return userSummaryConfirm(data, etatDetails, refController);
  };

  const steps = [
    {
      title: 'Infraction',
      content: JUSTICE_ACTIVE_BOOL ? (
        <FicheInfractionForm
          initialValues={data.ficheInfraction}
          setValues={(values: FicheInfractionFormValues): void => {
            setData(data => {
              return {
                ...data,
                ficheInfraction: values,
              };
            });
          }}
          goNextForm={goNextTab}
          natureMefLibelleCourt={branche?.nature}
        />
      ) : (
        <FicheInfractionFormSansJustice
          initialValues={data.ficheInfraction}
          setValues={(values: FicheInfractionFormValues): void => {
            setData(data => {
              return {
                ...data,
                ficheInfraction: values,
              };
            });
          }}
          goNextForm={goNextTab}
        />
      ),
      icon: <RiFileTextLine />,
    },
    {
      title: 'Véhicule',
      content: (
        <FicheVehiculeForm
          values={data.ficheVehicule}
          setValues={(values: FicheVehiculeFormValues): void => {
            setData(data => {
              return {
                ...data,
                ficheVehicule: values,
              };
            });
          }}
          goNextForm={goNextTab}
          goPreviousForm={goPreviousTab}
        />
      ),
      icon: <RiCarLine />,
    },
    {
      title: 'Entrée en fourrière',
      content: (
        <FicheFourriereForm
          values={data.ficheFourriere}
          formValues={data}
          dateConstatation={data.ficheInfraction.dateConstatation}
          dateRedaction={data.ficheInfraction.dateRedaction}
          setValues={(values: FicheFourriereFormValues): void => {
            setData(data => {
              return {
                ...data,
                ficheFourriere: values,
              };
            });
          }}
          goPreviousForm={goPreviousTab}
          onCreationSucceed={onCreationSucceed}
          userConfirm={userConfirm}
        />
      ),
      icon: <HomeOutlined />,
    },
  ];

  if (shouldDisplayJusticeFeatureBasedOnBranche(JUSTICE_ACTIVE_BOOL, branche)) {
    steps.splice(2, 0, {
      title: 'Judiciaire',
      content: (
        <FicheJudiciaireForm
          initialValues={data.FicheJudiciaire}
          setValues={(values: FicheJudiciaireFormValues): void => {
            setData(data => {
              return {
                ...data,
                FicheJudiciaire: values,
              };
            });
          }}
          goNextForm={goNextTab}
          goPreviousForm={goPreviousTab}
          motifMefId={data.ficheInfraction.idMotifMef}
          branche={branche}
        />
      ),
      icon: <RiScales3Line />,
    });
  }

  return (
    <div className={'FicheDescriptive'}>
      <AntdSteps type="default" current={indexTab} onChange={setIndexTab}>
        {steps.map((item, index) => (
          <Step
            key={item.title}
            title={item.title}
            icon={item.icon}
            disabled={index >= indexTab}
          />
        ))}
      </AntdSteps>
      <div className="content">{steps[indexTab].content}</div>
    </div>
  );
}
